<template>
    <div class="qa-annotations">
        <CollapsibleWidget content-padding="none" :is-opened="isExpanded" @onChange="setIsExpanded">
            <template #name>
                <span class="qa-annotations__title">
                    <slot name="titleContent">Annotations ({{ annotations.length }})</slot>
                </span>
            </template>

            <template #controls>
                <QaUserStatusIndicators :users="usersWithStatus" />
            </template>

            <div class="qa-annotations__annotations">
                <div v-if="!hasAnnotations" class="qa-annotations__no-annotations-message">
                    <slot name="emptyMessageContent">
                        <p>There are currently no internal annotations.</p>
                    </slot>
                </div>

                <QaSingleAnnotation
                    v-for="annotation of filteredAnnotations"
                    v-else
                    :key="annotation._id"
                    :annotation="annotation"
                    :is-selected="checkIfAnnotationIsSelected(annotation)"
                    @annotationDeleted="removeAnnotation"
                    @annotationSelected="selectAnnotation"
                    @annotationUpdated="replaceAnnotation"
                />
            </div>
        </CollapsibleWidget>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { ComputedDeliverableUserQAStatus } from "hoxton-types";

import QaUserStatusIndicators from "@/components/Qa/QaUserStatusIndicators.vue";
import QaSingleAnnotation from "@/components/Qa/QaSingleAnnotation.vue";
import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import { AnnotationStatus } from "@/enums/annotations";
import type { QaAnnotation } from "@/types/types";

export default defineComponent({
    components: {
        CollapsibleWidget,
        QaSingleAnnotation,
        QaUserStatusIndicators
    },
    props: {
        annotationType: {
            type: String,
            required: true
        },
        annotations: {
            required: true,
            type: Array as PropType<QaAnnotation[]>
        },
        selectedAnnotation: {
            type: [Object, null] as PropType<QaAnnotation | null>
        },

        showResolved: {
            type: Boolean,
            default: false
        },

        usersWithStatus: {
            required: true,
            type: Array as PropType<ComputedDeliverableUserQAStatus[]>
        }
    },

    emits: ["annotationDeleted", "annotationSelected", "annotationUpdated"],

    data() {
        return {
            isExpanded: true
        };
    },
    computed: {
        filteredAnnotations() {
            return this.showResolved
                ? this.annotations
                : this.annotations.filter(annotation => annotation.status === AnnotationStatus.Unresolved);
        },

        hasAnnotations() {
            return this.filteredAnnotations.length > 0;
        }
    },
    watch: {
        annotations() {
            this.setIsExpanded(true);
        },
        selectedAnnotation() {
            if (this.selectedAnnotation && this.selectedAnnotation.type === this.annotationType) {
                this.setIsExpanded(true);
            }
        }
    },
    methods: {
        checkIfAnnotationIsSelected(annotation: QaAnnotation) {
            return this.selectedAnnotation !== null && annotation._id === this.selectedAnnotation?._id;
        },
        removeAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationDeleted", annotation);
        },
        replaceAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationUpdated", annotation);
        },
        selectAnnotation(annotation: QaAnnotation) {
            this.$emit("annotationSelected", annotation);
        },
        setIsExpanded(isExpanded: boolean) {
            this.isExpanded = isExpanded;
        }
    }
});
</script>

<style lang="scss">
@use "@style/mixins" as *;
@use "@style/variables" as *;

.qa-annotations {
    .ivu-collapse > .ivu-collapse-item {
        border: none;
        border-bottom-color: transparent;

        &.ivu-collapse-item-active {
            border-bottom-color: $grey3;
        }

        > .ivu-collapse-header {
            border-bottom: 1px solid $grey3;
        }
    }

    .collapsible-widget.ivu-collapse .ivu-collapse-content > .ivu-collapse-content-box {
        border-bottom: 1px solid $grey3;
    }
}

.qa-annotations__annotations {
    background: $cmp-light-bg-color;
    padding: $spacing-small $spacing-small 0;

    @include clearfix();
}

.qa-annotations__title {
    font-size: $font-size-base;
}

.qa-annotations__no-annotations-message {
    background: $white;
    border: 1px dashed $grey3;
    color: $grey5;
    margin: 0 0 $spacing-small;
    padding: $spacing $spacing $spacing-small;

    > p {
        font-size: $font-size-small;
        margin-bottom: $spacing-small;
    }
}
</style>
